import { createContext, useContext, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PublishedStatement } from '@peruselab/api-client/molecules';
import { Icon } from '../../../components/Icon';
import { statementKey } from './factsheetText';
import { StatementEditing } from './statementEditing';

/**
 * #910 (ADR 0017 D11): "Discuss" on a statement, and which statement to bring into view (a comment
 * anchored to it was tapped, or the address names it). Provided by the factsheet; absent elsewhere.
 */
export const StatementDiscussion = createContext<{
  readonly onDiscuss?: (statement: PublishedStatement, key: string) => void;
  readonly focusKey?: string | null;
  /** #947: a statement that's an assertion is brought into view by its assertion id. */
  readonly focusAssertionId?: string | null;
}>({});

/** D1: statements shown before "Show all N". */
const SHOWN = 3;

/** #908: statements longer than this start collapsed (HSDB's toxicity summary runs to 4,600 characters). */
const LONG = 500;

export function Statement({ statement }: { readonly statement: PublishedStatement }) {
  const { t } = useTranslation();
  const { onDiscuss, focusKey, focusAssertionId } = useContext(StatementDiscussion);
  const { onCorrect, onWithdraw } = useContext(StatementEditing);
  const [open, setOpen] = useState(false);
  const long = statement.text.length > LONG;
  const key = statementKey(statement.source, statement.text);
  const focused =
    focusKey === key || (statement.assertionId !== undefined && focusAssertionId === statement.assertionId);
  const itemRef = useRef<HTMLLIElement | null>(null);
  useEffect(() => {
    if (focused) itemRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [focused]);
  return (
    <li
      id={key}
      ref={itemRef}
      className={`published-statement${focused ? ' published-statement--focused' : ''}`}
      data-statement-key={key}
      {...(statement.assertionId ? { 'data-assertion-id': statement.assertionId } : {})}
    >
      {/* A statement is in the language it was written in -- PubChem's are English (#897, #947). */}
      <p
        lang={statement.language ?? 'en'}
        dir="auto"
        className={long && !open ? 'published-statement-clamped' : undefined}
      >
        {statement.text}
      </p>
      <div className="published-statement-footer">
        <cite className="published-statement-source">
          {statement.sourceUrl ? (
            <a href={statement.sourceUrl} target="_blank" rel="noopener noreferrer" className="button-with-icon">
              <span>{statement.source}</span>
              <Icon name="openInNew" size="0.85rem" />
            </a>
          ) : (
            statement.source
          )}
        </cite>
        {long ? (
          <button type="button" className="discussion-link-button" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? t('moleculePage.readLess') : t('moleculePage.readMore')}
          </button>
        ) : null}
        {/* #947 phase 2: how a person's statement stands, and whether it's theirs alone. */}
        {statement.standing && statement.standing !== 'SOURCED' ? (
          <span className={`assertion-standing assertion-standing--${statement.standing.toLowerCase()}`}>
            {t(`properties.standing.${statement.standing}`)}
          </span>
        ) : null}
        {statement.audience === 'PRIVATE' ? (
          <span className="comment-badge">
            <Icon name="lock" size="0.8rem" /> {t('properties.audience.PRIVATE')}
          </span>
        ) : null}
        {statement.mine && onCorrect ? (
          <button
            type="button"
            className="icon-button"
            onClick={() => onCorrect(statement)}
            aria-label={t('properties.correct')}
            title={t('properties.correct')}
          >
            <Icon name="edit" />
          </button>
        ) : null}
        {statement.mine && onWithdraw ? (
          <button
            type="button"
            className="icon-button"
            onClick={() => onWithdraw(statement)}
            aria-label={t('properties.withdraw')}
            title={t('properties.withdraw')}
          >
            <Icon name="delete" />
          </button>
        ) : null}
        {onDiscuss ? (
          <button
            type="button"
            className="icon-button"
            aria-label={t('moleculePage.discuss')}
            title={t('moleculePage.discussStatement')}
            onClick={() => onDiscuss(statement, key)}
          >
            <Icon name="forum" />
          </button>
        ) : null}
      </div>
    </li>
  );
}

export function FactsSection({
  title,
  statements,
  shownFirst = SHOWN,
  nested = false
}: {
  readonly title: string;
  readonly statements: readonly PublishedStatement[];
  readonly shownFirst?: number;
  /** Inside another section (Safety's toxicity summary): an h3, not an h2. */
  readonly nested?: boolean;
}) {
  const { t } = useTranslation();
  const headingId = useId();
  const [all, setAll] = useState(false);
  const { focusKey, focusAssertionId } = useContext(StatementDiscussion);
  // A statement to bring into view that "Show all" is hiding: show all.
  const hiddenFocus =
    !all &&
    statements
      .slice(shownFirst)
      .some(
        (statement) =>
          (focusKey != null && statementKey(statement.source, statement.text) === focusKey) ||
          (focusAssertionId != null && statement.assertionId === focusAssertionId)
      );
  useEffect(() => {
    if (hiddenFocus) setAll(true);
  }, [hiddenFocus]);
  if (statements.length === 0) return null;
  const shown = all ? statements : statements.slice(0, shownFirst);
  return (
    <section className="published-facts-section" aria-labelledby={headingId}>
      {nested ? <h3 id={headingId}>{title}</h3> : <h2 id={headingId}>{title}</h2>}
      <ul className="published-statements">
        {shown.map((statement, index) => (
          <Statement key={index} statement={statement} />
        ))}
      </ul>
      {statements.length > shownFirst ? (
        <button type="button" className="discussion-link-button" aria-expanded={all} onClick={() => setAll(!all)}>
          {all ? t('moleculePage.showFewer') : t('moleculePage.showAll', { count: statements.length })}
        </button>
      ) : null}
    </section>
  );
}
